import Alpine from 'alpinejs';

function buildWebSocketUrl(path: string): string {
  const hostname = window.location.hostname;
  const port = window.location.port;
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${hostname}:${port}${path}`;
}

export const assistantStreaming = (
  initialQuestion: string | null,
  webSocketUrl: string,
  autoFocus: boolean = true,
) => ({
  // State
  webSocketUrl: buildWebSocketUrl(webSocketUrl),
  autoFocus: autoFocus,
  socket: null as WebSocket | null,
  sessionId: null as string | null,
  message: initialQuestion || '',
  messages: [] as Array<{
    role: string;
    content: string;
    id?: string;
    choices?: string[];
    matches?: Array<{ href: string; title: string; subtitle: string; icon?: string }>;
  }>,
  showEmptyView: true,
  showDisclaimer: false,
  isStreaming: false,
  streamingContent: '',
  choices: [] as string[],
  _destroyed: false,
  _keepaliveInterval: null as ReturnType<typeof setInterval> | null,
  _escHandler: null as ((e: KeyboardEvent) => void) | null,
  _scrollObserver: null as MutationObserver | null,

  init() {
    this.openSocket();
    this._setupModalBehaviors();

    // Keepalive ping
    this._keepaliveInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 5000);

    // Auto-focus input (only if autoFocus is enabled - disabled for embedded view)
    if (this.autoFocus) {
      setTimeout(() => {
        const input = this.$refs.messageInput as HTMLInputElement;
        if (input) input.focus();
      }, 100);
    }

    // Send initial question if provided
    if (this.message) {
      this.sendMessage();
    }
  },

  destroy() {
    this._destroyed = true;
    this._cleanupModalBehaviors();
    if (this._keepaliveInterval) {
      clearInterval(this._keepaliveInterval);
      this._keepaliveInterval = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  },

  _setupModalBehaviors() {
    // Only apply modal behaviors if autoFocus is true (modal view, not embedded)
    if (!this.autoFocus) {
      return;
    }

    // Prevent body scroll while modal is open
    document.body.style.overflow = 'hidden';

    // Close on escape
    this._escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this._closeModal();
      }
    };
    document.addEventListener('keydown', this._escHandler);

    // Restore scroll when modal is removed from DOM
    this._scrollObserver = new MutationObserver(() => {
      if (!document.getElementById('ai-modal')) {
        document.body.style.overflow = '';
        this._scrollObserver?.disconnect();
      }
    });
    this._scrollObserver.observe(document.body, { childList: true });
  },

  _cleanupModalBehaviors() {
    document.body.style.overflow = '';
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
      this._scrollObserver = null;
    }
  },

  _closeModal() {
    const modal = document.getElementById('ai-modal');
    if (modal) modal.remove();
  },

  openSocket() {
    if (this._destroyed) return;

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    const url = new URL(this.webSocketUrl);
    url.searchParams.set('streaming', 'true');
    if (this.sessionId) {
      url.searchParams.set('session-id', this.sessionId);
    }
    this.socket = new WebSocket(url.toString());

    const connectionTimeout = setTimeout(() => {
      if (this.socket?.readyState === WebSocket.CONNECTING) {
        console.error('WebSocket connection timeout');
        this.socket.close();
      }
    }, 10000);

    this.socket.addEventListener('open', () => {
      clearTimeout(connectionTimeout);
    });

    this.socket.addEventListener('error', (e) => {
      console.error('WebSocket error:', e);
      clearTimeout(connectionTimeout);
    });

    this.socket.addEventListener('close', () => {
      clearTimeout(connectionTimeout);
    });

    this.socket.addEventListener('message', (e) => {
      this.handleMessage(JSON.parse(e.data));
    });
  },

  handleMessage(msg: any) {
    switch (msg.type) {
      case 'init':
        this.sessionId = msg['session-id'];
        break;

      case 'user-message-saved':
        this.showEmptyView = false;
        this.messages.push({
          role: 'user',
          content: msg.content,
          id: msg['message-id'],
        });
        this.scrollToBottom();
        break;

      case 'stream-start':
        this.isStreaming = true;
        this.streamingContent = '';
        break;

      case 'stream-delta':
        this.streamingContent += msg.delta;
        this.scrollToBottom();
        break;

      case 'stream-choices':
        // Store choices to display as buttons
        this.choices = msg.choices || [];
        break;

      case 'stream-complete':
        this.isStreaming = false;
        // Backend sends clean content (marker already stripped)
        this.messages.push({
          role: 'assistant',
          content: msg.content || this.streamingContent,
          id: msg['message-id'],
          choices: this.choices.length > 0 ? [...this.choices] : undefined,
          matches: msg.matches || undefined,
        });
        this.streamingContent = '';
        this.choices = [];
        this.scrollToBottom();
        break;

      case 'stream-error':
        this.isStreaming = false;
        this.streamingContent = '';
        console.error('Stream error:', msg.error);
        break;

      case 'pong':
        break;

      default:
        console.log('Unknown message type:', msg.type);
    }
  },

  sendMessage() {
    const content = this.message.trim();
    if (!content) return;

    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    this.socket.send(
      JSON.stringify({
        type: 'send-message',
        content: content,
        'session-id': this.sessionId,
      }),
    );

    this.message = '';
  },

  sendPrompt(e: Event) {
    const target = e.target as HTMLElement;
    const prompt = target.dataset?.prompt || target.textContent;
    if (prompt) {
      this.message = prompt;
      this.sendMessage();
    }
  },

  selectChoice(choice: string) {
    this.message = choice;
    this.sendMessage();
  },

  scrollToBottom() {
    this.$nextTick(() => {
      const chatView = this.$refs.chatView as HTMLElement;
      if (chatView) {
        chatView.scrollTop = chatView.scrollHeight;
      }
    });
  },
});

// Register with Alpine - handles both cases:
// 1. Loaded before Alpine.start() (embedded view) - use alpine:init event
// 2. Loaded after Alpine.start() (htmx modal) - register directly and init tree
document.addEventListener('alpine:init', () => {
  Alpine.data('assistantStreaming', assistantStreaming);
});
